/**
 * router/routes/print.routes.ts
 * Top-level print routes — NOT nested under AppShell (sidebar/topbar would
 * clutter window.print() output), but still authenticated (no meta.public).
 * Split out of router/index.ts (openspec/arquitectura/convencion-modulo.md §4).
 */
import type { RouteRecordRaw } from 'vue-router';

export const printRoutes: RouteRecordRaw[] = [
  // add-locx-daily-report-print: top-level (no AppShell chrome — sidebar/topbar
  // would clutter window.print() output), but still authenticated (no meta.public).
  {
    path: '/servicios/orders/:id/daily-reports/:reportId/print',
    name: 'daily-report-print',
    component: () => import('../../views/DailyReportPrintView.vue'),
    meta: { requiresCapability: 'locx.parte.leer' },
  },

  // Transporte de Pasajeros (2026-07-13): generic order print, same "no
  // AppShell chrome" reasoning as daily-report-print above. Auto-fires
  // window.print() on load (see ServiceOrderPrintView.vue) — no manual click
  // needed, just the grid's download icon.
  {
    path: '/servicios/orders/:id/print',
    name: 'service-order-print',
    component: () => import('../../views/ServiceOrderPrintView.vue'),
    meta: { requiresCapability: 'locx.servicio.leer' },
  },

  // fleet-qr-compliance PR5: single-asset QR sticker print, same "no
  // AppShell chrome" posture as the two print routes above.
  {
    path: '/servicios/assets/:id/qr-print',
    name: 'asset-qr-print',
    component: () => import('../../views/AssetQrPrintView.vue'),
    meta: { requiresCapability: 'locx.activo.leer' },
  },

  // Compras — orden de compra descargable/imprimible (blueprint-compras.md
  // §6 "Descargar PDF"), mismo patrón "sin AppShell + window.print() auto"
  // que los tres de arriba (ServiceOrderPrintView.vue). :orderId identifica
  // la OC (una por proveedor); :caseId trae el contexto del caso.
  {
    path: '/compras/:caseId/oc/:orderId/print',
    name: 'compras-oc-print',
    component: () => import('../../views/compras/ComprasPurchaseOrderPrintView.vue'),
    meta: { requiresCapability: 'requi.solicitud.leer' },
  },

  // PermisoPrintView.vue — impresión de Permiso de Trabajo (PTW), replicando
  // la estructura del documento real de referencia (Pluspetrol PDT-ARNQ7A).
  // Mismo patrón "sin AppShell + window.print() auto" que los cuatro de arriba.
  {
    path: '/servicios/permisos/:id/print',
    name: 'permiso-print',
    component: () => import('../../views/servicios/PermisoPrintView.vue'),
    meta: { requiresCapability: ['locx.permiso.leer', 'locx.permiso.firmar'] },
  },

  // T8 (odd/tasks/cuentas-pagar-detalle-imputacion-reporte.md) — Orden de
  // Pago imprimible, mismo patrón "sin AppShell + window.print() auto" que
  // los print views de arriba. `:id` (proveedorId) mantiene la URL anidada
  // bajo el proveedor (consistente con cuentas-pagar-proveedor-detalle) aunque
  // GetPagoDetalleUseCase.ts solo necesita `:movimientoId` para resolver todo.
  // Capability mirrors the backend guard on GET /sumi/cuentas-pagar/pagos/:id
  // (movimiento.routes.ts, `sumi.saldo.leer` — read-only).
  {
    path: '/sumi/cuentas-pagar/proveedores/:id/pagos/:movimientoId/imprimir',
    name: 'cuentas-pagar-pago-imprimir',
    component: () => import('../../views/sumi/cuentas-pagar/OrdenDePagoPrintView.vue'),
    meta: { requiresCapability: 'sumi.saldo.leer' },
  },
];
